import useSWR from 'swr'
import {
  asLeadId,
  asPipelineId,
  type LeadDetail,
  type LeadId,
  type LudusLeadId,
  type PipelineId,
} from '@ludus/core'
import { fetchLeadDetail } from '@/lib/api/lead-detail.ts'
import { fetchSale } from '@/lib/api/ludus-leads.ts'

/**
 * A derived card's spec sheet: **its sibling's** (Q17, plan D6).
 *
 * A card a flow created has no source record of its own — no `externalRef`, no
 * adapter, nothing to describe — but the sale it belongs to usually does. The
 * Delivery card's Details tab therefore frames the Orders card's `describeLead`
 * document, because that is where the thing being delivered is actually
 * specified.
 *
 * **The sibling is chosen by `hasAdapter`, not by guessing.** The sale view
 * carries that flag per board precisely so the console can tell which card can
 * answer a describe call; picking "the first other card" would send a describe
 * to a native board that will answer `not_supported` and leave the tab empty
 * for a different reason than the one it started with.
 *
 * Nothing is fetched unless it is needed: no cycle, or a card with its own
 * detail, and both requests stay unmade — SWR is keyed `null` in those cases,
 * so this costs a derived card two reads and every other card none.
 */

export interface SiblingDetail {
  detail: LeadDetail | null
  /** Which card answered, so the tab can say whose sheet this is. */
  siblingLeadId: LeadId | null
  /**
   * …and on which board — every read that follows (a photo, the media proxy)
   * is addressed by pipeline *and* lead, and the sibling's pipeline is the one
   * with the adapter. Addressing it by the derived card's board asks a native
   * pipeline for a file it has never heard of.
   */
  siblingPipelineId: PipelineId | null
  siblingBoardName: string | null
  isLoading: boolean
}

export function useSiblingDetail(
  ludusLeadId: LudusLeadId | null,
  selfLeadId: LeadId,
  /** Skipped entirely when the card has a describe of its own. */
  enabled: boolean,
): SiblingDetail {
  const saleSwr = useSWR(
    enabled && ludusLeadId !== null ? `/ludus-leads/${ludusLeadId}` : null,
    () => fetchSale(ludusLeadId as LudusLeadId),
  )

  const sibling = (() => {
    const sale = saleSwr.data
    if (sale === undefined) return null
    for (const card of sale.cards) {
      if (card.id === String(selfLeadId)) continue
      const board = sale.boards[card.pipelineId]
      if (board?.hasAdapter !== true) continue
      return { leadId: card.id, pipelineId: card.pipelineId, boardName: board.name }
    }
    return null
  })()

  const detailSwr = useSWR(
    sibling === null ? null : `/pipelines/${sibling.pipelineId}/leads/${sibling.leadId}/detail`,
    () => fetchLeadDetail(asPipelineId(sibling!.pipelineId), asLeadId(sibling!.leadId)),
  )

  return {
    detail: detailSwr.data ?? null,
    siblingLeadId: sibling === null ? null : asLeadId(sibling.leadId),
    siblingPipelineId: sibling === null ? null : asPipelineId(sibling.pipelineId),
    siblingBoardName: sibling?.boardName ?? null,
    isLoading: saleSwr.isLoading || detailSwr.isLoading,
  }
}
